
import { mapGetters, mapState, mapActions } from 'vuex';
import * as XLSX from 'xlsx';
import moment from 'moment';
import { weatherObj } from '../../../util/weatherIcons'; // adjust path
// import SkeletonTableLoader from '@/components/SkeletonTableLoader.vue';
import {
  mergeByIndexDynamic,
  groupByDateWithStores
} from '@/helper/helperFunction';

export default {
  // components: { SkeletonTableLoader },
  // eslint-disable-next-line vue/require-prop-types
  props: ['lastTimeStamp'],

  data() {
    return {
      refreshTimeout: null,
      columns: [],
      rows: [],
      pagination: {
        page: 1,
        rowsPerPage: 20
      },
      isLoading: false,
      zoneData: [] // ✅ async processed zone data
    };
  },

  computed: {
    ...mapState('weather', ['weather', 'weatherCompare', 'weatherApi']),
    ...mapState('store', ['stores']),
    ...mapState('user', ['kpiPreferences']),
    ...mapState('distribution', ['tableDataReport']),
    ...mapGetters('filter', [
      'getStartingPeriod',
      'getEndingPeriod',
      'getStartingCompared',
      'getEndingCompared',
      'getStoreCodesList',
      'getDaysOfWeekList'
    ]),
    zone() {
      return this.zoneData || [];
    },
    visibleColumns() {
      return this.columns?.filter(col => col.visible);
    },
    paginatedRows() {
      const start = (this.pagination.page - 1) * this.pagination.rowsPerPage;
      const end = start + this.pagination.rowsPerPage;
      return this.rows.slice(start, end);
    },

    // ✅ total number of pages
    pagesNumber() {
      return Math.ceil(this.rows.length / this.pagination.rowsPerPage);
    }
  },

  watch: {
    tableDataReport: {
      async handler(val) {
        // run in async so UI & APIs don't freeze
        const [data, dataC] = await Promise.all([
          groupByDateWithStores(val.kpis),
          groupByDateWithStores(val.kpisCompare, true)
        ]);

        const d = mergeByIndexDynamic(data, dataC);
        this.zoneData = d;
        this.addZoneMethod();
      },
      deep: true
    },
    weather() {
      this.furtherHandler();
    },
    getStartingPeriod() {
      this.queueRefresh();
    },
    getEndingPeriod() {
      this.queueRefresh();
    },
    getStartingCompared() {
      this.queueRefresh();
    },
    getEndingCompared() {
      this.queueRefresh();
    },
    getStoreCodesList() {
      this.queueRefresh();
    },
    getDaysOfWeekList() {
      this.queueRefresh();
    },
    lastTimeStamp(newVal) {
      this.furtherHandler();
    }
  },

  mounted() {
    setTimeout(() => {
      this.loadData();
    }, 1000);
  },
  methods: {
    ...mapActions('distribution', ['fetchDistributionByDate']),
    queueRefresh() {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = setTimeout(() => {
        this.loadData();
      }, 300);
    },
    sortFun(data) {
      const fixedKeys = ['date', 'store', 'weather'];
      const fixed = data?.filter(item => fixedKeys.includes(item.name));
      const rest = data?.filter(item => !fixedKeys.includes(item.name));
      const sortedRest = [...rest].sort((a, b) =>
        a.label.localeCompare(b.label)
      );
      return [...fixed, ...sortedRest];
    },
    furtherHandler() {
      this.rows = this.rows?.map(i => {
        i.weather = '-';
        const s = i.stores?.map(el => {
          const weather =
            this.weatherApi?.[el?.dateWeather]?.[el?.city] || null;
          return {
            ...el,
            weather: !weather
              ? '-'
              : `${weatherObj[weather?.icon]} ${Math.round(weather?.temp)}°C`
          };
        });
        return { ...i, stores: s };
      });
    },
    onToggleChange(kpiName, newValue) {
      const comparisonCol = this.columns?.find(
        col => col.name === `${kpiName}C`
      );
      if (comparisonCol) {
        comparisonCol.visible = newValue;
      }
    },
    async loadData() {
      try {
        this.isLoading = true;
        await this.fetchDistributionByDate({
          startDate: this.getStartingPeriod,
          endDate: this.getEndingPeriod,
          startDateC: this.getStartingCompared,
          endDateC: this.getEndingCompared,
          storeCodes: this.getStoreCodesList,
          daysOfWeek: this.getDaysOfWeekList,
          kpis: 'sls,trf,cn,rov',
          table: true
        });
        // this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
        console.log('error', error);
      }
    },
    dateCount(dS, dE) {
      const startDate = moment(dS);
      const endDate = moment(dE);
      const diffDays = endDate.diff(startDate, 'days') + 1;
      return diffDays;
    },
    groupByBaseField(array) {
      const grouped = {};
      const baseOrder = [];
      for (const item of array) {
        const field = item.field;
        const base = field.endsWith('C') ? field.slice(0, -1) : field;
        if (!grouped[base]) {
          grouped[base] = [];
          baseOrder.push(base);
        }
        grouped[base].push(item);
      }
      return baseOrder.flatMap(base => grouped[base]);
    },
    addZoneMethod() {
      this.rows = [];
      this.columns = [];
      Object.keys(this.zone[0])?.map(key => {
        const check = key.endsWith('C');
        const c = check ? key?.slice(0, -1) : key;
        const find = this.kpiPreferences?.find(i => i.metricKey == c);
        const key2 = key === 'stores' ? 'store' : key;
        const visibleKpis = [
          'date',
          'stores',
          'sls',
          'slsC',
          'trf',
          'trfC',
          'cn',
          'cnC'
        ];
        const columns = [
          ...this.columns,
          {
            name: key2,
            label:
              key === 'stores'
                ? this.$store.state.user.translate.stores
                : key === 'date'
                ? this.$store.state.user.translate.kpi_comparsion_date
                : check
                ? `${find?.title} ${this.$store.state.user.translate.kpi_comparisons}`
                : find?.title,
            field: key2,
            visible: visibleKpis?.includes(key) || false
          }
        ];
        this.columns = this.groupByBaseField(columns)?.filter(
          item => item.label && item.field != 'st' && item.field != 'stC'
        );
      });
      this.columns?.splice(2, 0, {
        name: 'weather',
        label: this.$store.state.user.translate.weather,
        field: 'weather',
        visible: false
      });
      this.rows = this.zone?.map(item => {
        return { ...item, expand: false };
      });
      this.isLoading = false;
      this.furtherHandler();
    },
    toggleExpand(row) {
      row.expand = !row.expand;
    },
    validateNumber(num) {
      return !isFinite(num) || isNaN(num) ? 0 : num;
    },
    exportToCSV() {
      let exportData = [];
      const visibleCols = this.columns.filter(col => col.visible);
      this.rows.forEach(row => {
        let rowData = {};
        visibleCols.forEach(col => {
          rowData[col.label] =
            col.name === 'store'
              ? this.$store.state.user.translate.all_selected_stores
              : row[col.name] || '-';
        });
        exportData.push(rowData);
        if (row.stores && row.stores.length > 0) {
          row.stores.forEach(subZone => {
            let subZoneData = {};
            visibleCols.forEach(col => {
              subZoneData[col.label] = subZone[col.name] || '-';
            });
            exportData.push(subZoneData);
          });
        }
      });
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const csvContent = XLSX.utils.sheet_to_csv(worksheet, {
        FS: ',',
        RS: '\r\n'
      });
      const utf8BOM = '\uFEFF' + csvContent;
      const blob = new Blob([utf8BOM], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${this.$store.state.user.translate.store_comparison_table} Selected_${this.getStartingPeriod}-${this.getEndingPeriod} Compared_${this.getStartingCompared}-${this.getEndingCompared}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    },
    exportToExcel() {
      let exportData = [];
      const visibleCols = this.columns.filter(col => col.visible);
      this.rows.forEach(row => {
        let rowData = {};
        visibleCols.forEach(col => {
          rowData[col.label] =
            col.name === 'store'
              ? this.$store.state.user.translate.all_selected_stores
              : row[col.name] || '-';
        });
        exportData.push(rowData);
        if (row.stores && row.stores.length > 0) {
          row.stores.forEach(subZone => {
            let subZoneData = {};
            visibleCols.forEach(col => {
              subZoneData[col.label] = subZone[col.name] || '-';
            });
            exportData.push(subZoneData);
          });
        }
      });
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        this.$store.state.user.translate.store_comparison_table
      );
      const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'binary' });
      function s2ab(s) {
        const buf = new ArrayBuffer(s.length);
        const view = new Uint8Array(buf);
        for (let i = 0; i < s.length; ++i) view[i] = s.charCodeAt(i) & 0xff;
        return buf;
      }
      const blob = new Blob([s2ab(wbout)], {
        type: 'application/octet-stream'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${this.$store.state.user.translate.store_comparison_table} Selected_${this.getStartingPeriod}-${this.getEndingPeriod} Compared_${this.getStartingCompared}-${this.getEndingCompared}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }
};
